// Search Bar UI module

import { LOGGING, TAG_SEARCH_SETTINGS } from '/settings.js';
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { parseUrlForSearchBarCityTags, sanitizeInput, isForbiddenChar, sanitizePaste, fetchWithCache } from '../utils/index.js';
import { updateUrlForSearch, parseUrlParams } from '../utils/url-utils.js';
import { DATA_PATHS } from '../dataPaths.js';
import { clearAllSpots } from '../map/map-visibility.js';
import { loadCitiesList, fetchTagsForCity, performSearch, findCityByLocation } from './header-search.js';
import { createIconElement } from '../utils/icon.js';
// import { createAreaSwitcherButton } from './header-area-switcher.js'; // Removed: Handled by index.js

let spotsData = [];
let selectedCity = null;
let currentStep = 2;
let selectedTag = '';
let tagTranslations = null;
let selectedTags = [];
let availableTags = [];

export async function initializeSearchBar(container) {
    // 1. Get Active Area (City)
    const { areaSlug } = parseUrlParams();
    if (!areaSlug) {
        console.error('Search Bar initialized without active city/area.');
        return;
    }

    // NOTE: We do NOT clear the container here anymore, index.js does it.
    // If container is not provided, we try to fallback (legacy support)
    if (!container) {
        container = document.getElementById('search-bar-container');
        if (container) container.innerHTML = ''; // Only clear if we are in legacy mode
    }
    if (!container) return;


    // Load cities to find our city object
    const cities = await loadCitiesList();
    function slugify(str) {
        return str.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    }
    selectedCity = cities.find(c => c.slug === areaSlug || slugify(c.display) === areaSlug);

    if (!selectedCity) {
        console.error('Active city not found in cities list:', areaSlug);
        return;
    }

    // Reset state
    spotsData = [];
    currentStep = 2;
    selectedTag = '';
    tagTranslations = null;

    // Main wrapper
    const searchBarWrapper = document.createElement('div');
    searchBarWrapper.className = 'search-bar-wrapper';
    // Ensure flex behavior for wrapper so it sits nicely next to switcher
    searchBarWrapper.style.display = 'flex';
    searchBarWrapper.style.alignItems = 'center';
    searchBarWrapper.style.gap = '10px';

    // Container for input and buttons
    const searchBarContainer = document.createElement('div');
    searchBarContainer.className = 'search-bar-container';

    // Main row for tag pills and input
    const mainRow = document.createElement('div');
    mainRow.className = 'search-bar-main-row';

    // Tag pills container
    const tagPills = document.createElement('div');
    tagPills.className = 'search-bar-tag-pills';
    tagPills.style.display = 'none';

    // Input
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'search-bar-input';
    input.placeholder = await getText('search.placeholder');

    // Search icon
    const searchBtn = document.createElement('button');
    searchBtn.className = 'search-bar-btn icon-btn';
    const searchImg = createIconElement('magnifying-glass-solid', 'icon');
    searchBtn.appendChild(searchImg);

    // Suggestions dropdown
    const suggestions = document.createElement('div');
    suggestions.className = 'search-bar-suggestions';
    suggestions.style.display = 'none';

    // --- Area Switcher Logic Removed (Moved to index.js) ---

    // Helper: get unique tags
    function getUniqueTags() {
        return availableTags;
    }

    function getTagTranslation(tag, lang) {
        if (typeof tag === 'string') return tag.replace(/_/g, ' ');
        if (lang === 'en') return tag.name_en || tag.name_es || '';
        return tag.name_es || tag.name_en || '';
    }

    // Helper: render tag pills
    async function renderTagPills() {
        tagPills.innerHTML = '';
        if (selectedTags.length === 0) {
            tagPills.style.display = 'none';
            input.placeholder = await getText('search.placeholder');
            input.disabled = false;
            searchBarContainer.classList.remove('search-bar-max-tags');
            return;
        }
        tagPills.style.display = 'flex';
        const lang = getCurrentLanguage ? getCurrentLanguage() : 'es';

        if (selectedTags.length >= TAG_SEARCH_SETTINGS.MAX_TAGS_PER_SEARCH) {
            searchBarContainer.classList.add('search-bar-max-tags');
        } else {
            searchBarContainer.classList.remove('search-bar-max-tags');
        }

        selectedTags.forEach((tag, idx) => {
            const pill = document.createElement('span');
            pill.className = 'search-bar-tag-pill';
            const tagObj = availableTags.find(t => t.id === tag.id || t.name_es === tag || t.name_en === tag || t === tag);
            pill.textContent = getTagTranslation(tagObj || tag, lang);

            const removeBtn = document.createElement('button');
            removeBtn.className = 'pill-remove icon-btn';
            const removeBtnImg = createIconElement('circle-xmark-regular', 'icon');
            removeBtn.appendChild(removeBtnImg);
            removeBtn.onclick = () => {
                selectedTags.splice(idx, 1);
                renderTagPills();
                input.disabled = false;
                input.focus();
            };
            pill.appendChild(removeBtn);
            tagPills.appendChild(pill);
        });

        if (selectedTags.length === 1) {
            input.placeholder = '';
            input.disabled = false;
        } else if (selectedTags.length >= TAG_SEARCH_SETTINGS.MAX_TAGS_PER_SEARCH) {
            input.placeholder = '';
            input.disabled = true;
        }
    }

    async function showSuggestions(list) {
        suggestions.innerHTML = '';
        if (list.length === 0) {
            suggestions.style.display = 'none';
            return;
        }
        const lang = getCurrentLanguage ? getCurrentLanguage() : 'es';
        for (const item of list) {
            const div = document.createElement('div');
            div.className = 'search-bar-suggestion-item';
            div.textContent = getTagTranslation(item, lang);

            if (selectedTags.some(sel => sel.id === item.id || sel === item.name_es || sel === item.name_en) || selectedTags.length >= TAG_SEARCH_SETTINGS.MAX_TAGS_PER_SEARCH) {
                div.classList.add('disabled');
                div.style.opacity = '0.5';
                div.style.pointerEvents = 'none';
            }
            div.onclick = async () => {
                if (!selectedTags.some(sel => sel.id === item.id || sel === item.name_es || sel === item.name_en) && selectedTags.length < TAG_SEARCH_SETTINGS.MAX_TAGS_PER_SEARCH) {
                    selectedTags.push(item);
                    renderTagPills();
                    input.value = '';
                    suggestions.style.display = 'none';
                    input.focus();
                }
            };
            suggestions.appendChild(div);
        }
        suggestions.style.display = 'block';
    }

    // Initialization: Load data for the pre-selected city
    async function initCityData() {
        try {
            const spotsUrl = DATA_PATHS.SPOTS({ cityId: selectedCity.id });
        } catch (e) {
            spotsData = [];
        }
        availableTags = await fetchTagsForCity(selectedCity.id);

        // Handle URL tags
        const { tags } = parseUrlForSearchBarCityTags();
        if (tags && tags.length > 0) {
            selectedTags = availableTags.filter(t => tags.includes(t.slug));
            renderTagPills();
        }
    }

    // Input event
    input.addEventListener('input', async () => {
        const val = sanitizeInput(input.value.trim().toLowerCase());
        const lang = getCurrentLanguage ? getCurrentLanguage() : 'es';
        const tags = getUniqueTags().filter(tag => getTagTranslation(tag, lang).toLowerCase().includes(val) && !selectedTags.some(sel => sel.id === tag.id || sel === tag.name_es || sel === tag.name_en));
        await showSuggestions(tags);
    });

    // Search button event
    searchBtn.addEventListener('click', async () => {
        if (selectedCity && (selectedTags.length > 0)) {
            import('../utils/clearAll.js').then(({ clearAllUI }) => clearAllUI());

            updateUrlForSearch(selectedCity, selectedTags);

            function getSlug(val) {
                if (!val) return '';
                if (val.slug) return val.slug;
                if (val.display) return slugify(val.display);
                if (val.name_en) return slugify(val.name_en);
                return '';
            }
            const citySlug = getSlug(selectedCity);
            const tagSlugs = selectedTags.map(getSlug);
            const results = await performSearch(citySlug, tagSlugs);
            document.dispatchEvent(new CustomEvent('searchResults', { detail: { results, searchedTags: tagSlugs } }));
            suggestions.style.display = 'none';
            selectedTags = [];
            renderTagPills();
            input.value = '';
            input.focus();
        }
    });

    // Hide suggestions on outside click
    document.addEventListener('click', (e) => {
        if (!searchBarWrapper.contains(e.target)) {
            suggestions.style.display = 'none';
        }
    });

    input.addEventListener('keydown', (e) => {
        if (isForbiddenChar(e.key)) e.preventDefault();
        if (e.key === 'Enter') searchBtn.click();
    });

    input.addEventListener('paste', (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text');
        const sanitized = sanitizePaste(text);
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value;
        input.value = value.slice(0, start) + sanitized + value.slice(end);
        input.selectionStart = input.selectionEnd = start + sanitized.length;
        input.dispatchEvent(new Event('input'));
    });

    // Append elements
    mainRow.appendChild(tagPills);
    mainRow.appendChild(input);
    searchBarContainer.appendChild(mainRow);
    searchBarContainer.appendChild(searchBtn);

    // Note: We append directly to container now (or container passed in index.js)
    // Actually, we append the wrapper to the container
    searchBarWrapper.appendChild(searchBarContainer);
    searchBarWrapper.appendChild(suggestions);

    container.appendChild(searchBarWrapper);

    // Start data load
    await initCityData();
}

export function resetSearchBarContent() {
    selectedTags = [];
    selectedTag = '';
    const wrapper = document.querySelector('.search-bar-wrapper');
    if (!wrapper) return;
    const input = wrapper.querySelector('.search-bar-input');
    if (input) {
        input.value = '';
        input.placeholder = 'Search...';
        input.disabled = false;
    }
    const tagPills = wrapper.querySelector('.search-bar-tag-pills');
    if (tagPills) {
        tagPills.innerHTML = '';
        tagPills.style.display = 'none';
    }
    const suggestions = wrapper.querySelector('.search-bar-suggestions');
    if (suggestions) suggestions.style.display = 'none';
} 
